import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "foregroundContent", "href", "linkComponent", "onClick", "tabIndex", "use"],
  _excluded2 = ["hovered"],
  _excluded3 = ["focused"];
import { useEffect, useMemo, useRef, useState } from 'react';
import { Clickable } from '../Clickable/Clickable';
import { Link } from '../Link/Link';
import { filterProps } from '../../../utils/filterProps';
import { FocusProvider } from '../../utils/FocusProvider';
import { HoverProvider } from '../HoverProvider';
import * as CustomRenderer from '../AbstractPageTemplate/customRenderer';
import styled from 'foundations-theming/utils';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { warnIfFragment } from '../../utils/warnIfFragment/warnIfFragment';
import devLogger from 'react-utils/devLogger';
import { FOCUSABLE } from '../../utils/dom';
import { rgba } from '../../utils/color';
import { HOVERLAY_TRANSITION_TIMING } from '../../../constants/times';
import { MERCURY_LAYER } from '../../constants';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const transitionTime = parseInt(HOVERLAY_TRANSITION_TIMING, 10);
const StyledHoverlay = styled.div.withConfig({
  displayName: "Hoverlay__StyledHoverlay",
  componentId: "sc-18le20v-0"
})(({
  $use,
  theme,
  $cssStyles
}) => {
  return Object.assign({
    position: 'relative',
    overflow: 'hidden'
  }, $use === 'full-card' && {
    marginBottom: theme.space['400'],
    marginTop: theme.space['0']
  }, $use === 'avatar' && {
    borderRadius: '100%'
  }, $cssStyles);
});
const StyledOverlay = styled.div.withConfig({
  displayName: "Hoverlay__StyledOverlay",
  componentId: "sc-18le20v-1"
})(({
  $use,
  $isVisible,
  theme
}) => {
  return Object.assign({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    color: theme.color['text-core-on-fill-default'],
    transition: 'background-color 0.2s ease, opacity 0.2s ease',
    zIndex: MERCURY_LAYER,
    backgroundColor: $isVisible ? rgba(theme.color['fill-accent-neutral-hover'], 0.85) : rgba(theme.color['fill-transparent-default'], 0)
  }, $use === 'full-card' && {
    bottom: theme.space['100'],
    borderRadius: theme.borderRadius['100']
  }, $use === 'bottom-half-card' && {
    borderBottomLeftRadius: theme.borderRadius['100'],
    borderBottomRightRadius: theme.borderRadius['100']
  }, $use === 'avatar' && Object.assign({}, theme.typography['heading-50'], {
    textDecoration: 'underline'
  }));
});
const StyledOverlayContent = styled.div.withConfig({
  displayName: "Hoverlay__StyledOverlayContent",
  componentId: "sc-18le20v-2"
})(({
  $isVisible,
  theme
}) => {
  return {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
    height: '100%',
    width: '100%',
    margin: theme.space['0'],
    transition: `opacity ${transitionTime}ms`,
    opacity: $isVisible ? 1 : 0,
    outline: 'none'
  };
});
const StyledChildren = styled.div.withConfig({
  displayName: "Hoverlay__StyledChildren",
  componentId: "sc-18le20v-3"
})(({
  $use,
  $isVisible,
  theme
}) => {
  return Object.assign({
    filter: $isVisible && $use !== 'avatar' ? 'blur(5px)' : 'blur(0)',
    transition: `filter ${transitionTime}ms steps(5)`
  }, $use === 'full-card' && {
    marginBottom: theme.space['100']
  });
});
const Overlay = ({
  use,
  href,
  onClick,
  isVisible,
  linkComponent,
  foregroundContent,
  children
}) => {
  const overlay = useMemo(() => /*#__PURE__*/_jsxs("span", {
    children: [/*#__PURE__*/_jsx(StyledOverlay, {
      $isVisible: isVisible,
      $use: use,
      children: /*#__PURE__*/_jsx(StyledOverlayContent, {
        $isVisible: isVisible,
        children: foregroundContent
      })
    }), /*#__PURE__*/_jsx(StyledChildren, {
      $isVisible: isVisible,
      $use: use,
      children: children
    })]
  }), [children, foregroundContent, isVisible, use]);
  if (href) {
    return CustomRenderer.render(linkComponent, {
      children: overlay,
      href,
      use: 'unstyled'
    });
  }
  if (onClick) {
    return /*#__PURE__*/_jsx(Clickable, {
      onClick: onClick,
      children: overlay
    });
  }
  return overlay;
};
export const Hoverlay = _ref => {
  let {
      children,
      foregroundContent,
      href,
      linkComponent = Link,
      onClick,
      tabIndex,
      use = 'default'
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const [hasTabbableContent, setHasTabbableContent] = useState(false);
  const hoverlayRef = useRef(null);
  const computedTabIndex = tabIndex || hasTabbableContent ? undefined : 0;
  useEffect(() => {
    if (!hoverlayRef.current) return;
    const childHasTabbableContent = hoverlayRef.current.querySelector(FOCUSABLE);
    if (childHasTabbableContent) {
      setHasTabbableContent(true);
    }
  }, []);
  if (process.env.NODE_ENV !== 'production') {
    if (href && onClick) {
      devLogger.warn({
        key: 'Hoverlay: href and onClick',
        message: 'Hoverlay: Cannot have both onClick and href props.'
      });
    }
  }
  warnIfFragment(children, 'Hoverlay');
  return /*#__PURE__*/_jsx(HoverProvider, Object.assign({}, rest, {
    children: hoverProviderProps => /*#__PURE__*/_jsx(FocusProvider, Object.assign({}, rest, {
      children: focusProviderProps => {
        const {
            hovered
          } = hoverProviderProps,
          hoverProviderRestProps = _objectWithoutPropertiesLoose(hoverProviderProps, _excluded2);
        const {
            focused
          } = focusProviderProps,
          focusProviderRestProps = _objectWithoutPropertiesLoose(focusProviderProps, _excluded3);
        const isVisible = Boolean(hovered || focused);
        return /*#__PURE__*/_jsx(StyledHoverlay, Object.assign({}, rest, domProps, hoverProviderRestProps, focusProviderRestProps, {
          $use: use,
          ref: hoverlayRef,
          tabIndex: computedTabIndex,
          $cssStyles: cssStyles,
          children: /*#__PURE__*/_jsx(Overlay, {
            use: use,
            href: href,
            onClick: onClick,
            isVisible: isVisible,
            linkComponent: linkComponent,
            foregroundContent: foregroundContent,
            children: children
          })
        }));
      }
    }))
  }));
};